import {PlayerDurum} from '@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes';
import {UyapEngeli} from '@/types';
import {UyapEngelKaynagi} from '@/plugins/uyap-plugin/UyapEngeli';

/**
 * UYAP "insan doğrulaması" uyarısı (engel; DEV-3572): imzacının SAF yardımcıları.
 *
 * Saf tutulur (store, DOM ve "@/main" import edilmez; repodaki spec'ler bu deseni kullanır). Engel botun
 * `botBeklemeSebebi` listesine GİRMEZ ve durum satırına (`uyapStatusMessage`) UYAP metni YAZILMAZ: host o satırı
 * 'kart', 'takılı', 'timeout', 'zaman', 'imzacı', 'program' kelimelerine göre değiştiriyor/siliyor ve UYAP'ın
 * ikinci metni "robot programları" içeriyor. Engel ayrı bir durum alanıdır (`state.uyapEngeli`).
 *
 * Kayıt YALNIZ pencere oturumunun deposundadır (sessionStorage; pencere yenilemede geri gelir). Kalıcı depo
 * KULLANILMAZ: imzacı kapatılıp açılınca kilit hatırlanmaz (K4). Kilidi yalnız kullanıcı eylemi kaldırır
 * (store `uyapEngeliniKaldir`).
 */

/** sessionStorage anahtarı. Değer JSON `{zaman, mesaj, kaynak}` (`diyalogAcik` YAZILMAZ). */
export const UYAP_ENGEL_DEPO_ANAHTARI = 'imzaciUyapEngeli';
export const UYAP_ENGEL_MESAJ_SINIRI = 600;
/** Panele giden geçici duyurunun süresi (sn). */
export const UYAP_ENGEL_PANEL_BILDIRIM_SN = 8;
/** Sunucu freni kaldırma ucu (Panel `UYAP_ENGEL_KALDIR_URL` ile AYNI değer). */
export const UYAP_ENGEL_FREN_YOLU = '/api/v1/uyap-engel';
export const UYAP_ENGEL_FREN_ZAMAN_ASIMI_MS = 3000;

/**
 * Kullanıcıya görünen metinlerin TEK kaynağı (K8, AYNEN; orta nokta U+00B7 ve çevresinde birer boşluk).
 * Durum satırına (uyapStatusMessage) HİÇBİRİ yazılmaz. UYAP'ın nasıl kaldırılacağına dair talimat YOKTUR.
 */
export const UYAP_ENGEL_METINLERI = {
    bildirimBaslik: 'UYAP insan doğrulaması istedi',
    bildirimGovde: 'İşlemler durduruldu; bekleyen dosyalar sırada.',
    diyalogBaslik: 'UYAP insan doğrulaması istedi; işlemler durduruldu',
    diyalogAciklama: "Bu nedenle UYAP'a istek göndermeyi bıraktık; bekleyen dosyalar sırada duruyor.",
    kartRozet: 'UYAP Engeli',
    kartAciklamaBagli: 'UYAP bağlı; işlem isteği gönderilmiyor',
    // K8 dışı mekanik varyant (engel + bağlantı yok)
    kartAciklamaBagliDegil: 'UYAP bağlı değil; işlem isteği gönderilmiyor',
    ayrinti: 'Ayrıntı',
    bant: "UYAP engeli nedeniyle durdu. Başlat'a basınca bir dosya denenir.",
    onayBaslik: 'Yeniden denensin mi?',
    onayMetin: 'UYAP uyarısı sürüyorsa bot ilk dosyada yeniden durur ve o dosya hatalı biter.',
    onayVazgec: 'Vazgeç',
    onayDene: 'Dene',
    kapat: 'Kapat',
    uyapiAc: "UYAP'ı aç",
    izinVer: 'Yeniden denemeye izin ver'
};

/** Düz metin: boşluklar tekilleşir (kontrol karakter regex'i YOK: eslint no-control-regex), en çok 600 karakter. */
export function engelMesajiniTemizle(ham: unknown): string {
    if (typeof ham !== 'string') {
        return '';
    }
    return ham.replace(/\s+/g, ' ').trim().slice(0, UYAP_ENGEL_MESAJ_SINIRI);
}

/**
 * Kayıt doğrulama: bozuk/eksik alan (null, dizi, `zaman` NaN/0/metin) null döner. Bilinmeyen `kaynak` 'gorev' olur.
 * `diyalogAcik` her zaman false: geri yükleme sessizdir (diyalog kapalı, bildirim ve ses yok).
 */
export function uyapEngeliniTemizle(ham: unknown): UyapEngeli | null {
    const g = ham as {zaman?: unknown, mesaj?: unknown, kaynak?: unknown} | null;
    if (!g || typeof g !== 'object' || typeof g.zaman !== 'number' || !isFinite(g.zaman) || g.zaman <= 0) {
        return null;
    }
    const kaynak: UyapEngelKaynagi = g.kaynak === 'yoklama' || g.kaynak === 'sunucu' ? g.kaynak : 'gorev';
    return {zaman: g.zaman, mesaj: engelMesajiniTemizle(g.mesaj), kaynak, diyalogAcik: false};
}

/** Kaydı okur; depo yok/erişim hatası/bozuk JSON/bozuk alan: null (kayıt yok). */
export function uyapEngeliniOku(depo: Storage | null): UyapEngeli | null {
    if (!depo) {
        return null;
    }
    try {
        const ham = depo.getItem(UYAP_ENGEL_DEPO_ANAHTARI);
        return ham === null ? null : uyapEngeliniTemizle(JSON.parse(ham));
    } catch (e) {
        return null;
    }
}

/** Kaydı yazar (`diyalogAcik` YAZILMAZ). Depo yok ya da erişim hatası sessizce yutulur (loglanır). */
export function uyapEngeliniYaz(depo: Storage | null, engel: UyapEngeli): void {
    if (!depo) {
        return;
    }
    try {
        depo.setItem(UYAP_ENGEL_DEPO_ANAHTARI, JSON.stringify({zaman: engel.zaman, mesaj: engel.mesaj, kaynak: engel.kaynak}));
    } catch (e) {
        console.warn('UYAP engeli kaydı yazılamadı:', e);
    }
}

export function uyapEngeliniSil(depo: Storage | null): void {
    if (!depo) {
        return;
    }
    try {
        depo.removeItem(UYAP_ENGEL_DEPO_ANAHTARI);
    } catch (e) {
        console.warn('UYAP engeli kaydı silinemedi:', e);
    }
}

function iki(sayi: number): string {
    return sayi < 10 ? '0' + sayi : String(sayi);
}

/** 'HH:MM' (yerel saat). toLocaleTimeString'e BAĞLI DEĞİL: test ortamının ICU'sundan bağımsız. */
export function engelSaatMetni(zaman: number): string {
    const t = new Date(zaman);
    return iki(t.getHours()) + ':' + iki(t.getMinutes());
}

/** Panele giden geçici mesaj (K8 panel şeridinin 'Bot' satırıyla AYNI metin). */
export function engelPanelMesaji(zaman: number): string {
    return 'Bot: UYAP engeli nedeniyle durdu · ' + engelSaatMetni(zaman);
}

/** Diyalogdaki özet satırı ("Takip Sorgula · 312 dosya sırada · 14:32"); bilinmeyen parça atlanır, 0 dosya yazılmaz. */
export function engelOzetMetni(zaman: number, paketAdi: string | null, siradaDosya: number): string {
    return [paketAdi, siradaDosya > 0 ? siradaDosya + ' dosya sırada' : null, engelSaatMetni(zaman)]
        .filter((parca) => parca !== null && parca !== '').join(' · ');
}

/**
 * `player` olayı kararı (sözleşme §4.5 tablosu). Karşılaştırmalar `===`: PlayerDurum.oto sayısal 0'dır (falsy tuzağı).
 *
 *  - 'uygula': olay bugünkü gibi işlenir (commit);
 *  - 'kaldir-uygula': kilit VARKEN çıplak SAYI 0 = kullanıcı eylemi (panelin Başlat'ı): kilit kalkar, sonra 'oto' uygulanır;
 *  - 'reddet': kilit VARKEN METİN "0" = otomatik başlatma ya da kabuğun yeniden oynatması: commit YOK;
 *  - 'yoksay': kilit VARKEN null/''/boşluk/false/[] (hepsi Number()'da 0): commit YOK, yayın YOK.
 *
 * Tür kuralı: kabuk tipi korur (canlı aktarımda sayı sayı kalır; yeniden oynatma HER ZAMAN metin/null'dır). Plugin'in
 * otomatik yolları `String(PlayerDurum.oto)` yayınlar. Biri otomatik yolu sayıya "düzeltirse" kilit sessizce atlanır;
 * üçüncü bir tüketici `Number(x) === oto` ile kullanıcı eylemi anlamı çıkarmamalıdır.
 */
export type PlayerOlayiKarari = 'uygula' | 'kaldir-uygula' | 'reddet' | 'yoksay';

export function playerOlayiKarari(ham: unknown, engelVar: boolean): PlayerOlayiKarari {
    if (!engelVar || Number(ham) !== PlayerDurum.oto) {
        return 'uygula';
    }
    if (typeof ham === 'number') {
        return 'kaldir-uygula';
    }
    if (typeof ham === 'string' && ham.trim() !== '') {
        return 'reddet';
    }
    return 'yoksay';
}

export interface BaslatBaglami {
    engelVar: () => boolean;
    onayIste: () => Promise<boolean>;
    engeliKaldirVeBaslat: () => Promise<boolean | void>;
    baslat: () => void;
}

/**
 * Kullanıcı Başlat'ının karar mantığı (K5/K8). true = bot başlatıldı ('oto' yayınlandı), false = kullanıcı vazgeçti
 * ya da kaldırma beklenirken yeniden kurulan YENİ kilit kullanıcıya kaldı ('oto' yayınlanmadı).
 * Engel yoksa bugünkü davranış (onaysız); engel varsa onay istenir, Dene ise kilit kalkar ve bot başlar.
 * `engeliKaldirVeBaslat` açıkça `false` dönerse 'oto' yayınlanmamıştır; hiçbir şey dönmezse (eski biçim) yayınlanmıştır.
 */
export async function baslatIsteginiIsle(b: BaslatBaglami): Promise<boolean> {
    if (!b.engelVar()) {
        b.baslat();
        return true;
    }
    if (!(await b.onayIste())) {
        return false;
    }
    return (await b.engeliKaldirVeBaslat()) !== false;
}
